import type { EstadoCanvas } from '../dominio/assistente.interface'
import { navegarPeloHost, type AlvoDeNavegacaoDoHost } from './acoplamento/navegacaoDoHost'
import { prepararNavegacaoHandoff, type HandoffRouteResolver } from './canvas/handoffNavigation'
import type { ResultadoDoPreenchimento } from './canvas/preenchimentoDeTela'

export type NavegacaoDoAssistente = Extract<EstadoCanvas, { type: 'handoff' }>

export interface OrigemDaAbertura {
  pessoaId: string
  empresaId: string
  sessaoId: string
  interacaoId: string
}

export interface ContextoDeAbertura extends OrigemDaAbertura {
  versao: 1
  identificadorRota: string
  identificadorTela?: string
  rascunhoOpaco?: string
}

export function identidadeDaAberturaCompativel (
  contexto: ContextoDeAbertura,
  atual: { pessoaId: string, empresaId: string, sessaoId: string | null }
): boolean {
  return contexto.pessoaId === atual.pessoaId &&
    contexto.empresaId === atual.empresaId &&
    contexto.sessaoId === atual.sessaoId
}

export interface OfertaDeNavegacao extends OrigemDaAbertura {
  canvas: NavegacaoDoAssistente
}

export type VerificarEquivalenciaDeRotas = (esperada: string, atual: string) => boolean

export function criarOfertaDeNavegacao (
  canvas: NavegacaoDoAssistente,
  origem: OrigemDaAbertura
): OfertaDeNavegacao {
  return { canvas: { ...canvas }, ...origem }
}

export function criarContextoDeAbertura (
  canvas: NavegacaoDoAssistente,
  origem: OrigemDaAbertura
): ContextoDeAbertura {
  return {
    versao: 1,
    ...origem,
    identificadorRota: canvas.identificador_rota,
    ...(canvas.identificador_tela ? { identificadorTela: canvas.identificador_tela } : {}),
    ...(canvas.rascunho_opaco ? { rascunhoOpaco: canvas.rascunho_opaco } : {})
  }
}

export async function abrirTelaRelacionada (
  canvas: NavegacaoDoAssistente,
  origem: OrigemDaAbertura,
  resolver: HandoffRouteResolver,
  navegar: (alvo: AlvoDeNavegacaoDoHost) => Promise<unknown> | unknown,
  aposNavegar?: () => void,
  obterRotaAtual?: () => string,
  rotasSaoEquivalentes: VerificarEquivalenciaDeRotas = (esperada, atual) => esperada === atual
): Promise<{ ok: true } | { ok: false, code: string }> {
  const preparo = prepararNavegacaoHandoff(canvas, resolver)
  if (!preparo.ok) return preparo
  const alvo: AlvoDeNavegacaoDoHost = {
    path: preparo.target.path,
    state: { assistentePontotelAbertura: { ...criarContextoDeAbertura(canvas, origem) } }
  }
  try {
    const falha = await navegarPeloHost(alvo, navegar)
    if (falha) return { ok: false, code: 'falha_ao_navegar' }
    if (obterRotaAtual && !rotasSaoEquivalentes(preparo.target.path, obterRotaAtual())) {
      return { ok: false, code: 'falha_ao_navegar' }
    }
    aposNavegar?.()
    return { ok: true }
  } catch {
    return { ok: false, code: 'falha_ao_navegar' }
  }
}

interface HistoricoComEstado {
  state: Record<string, unknown> | null
  replaceState: (estado: Record<string, unknown>, titulo: string) => void
}

function contextoValido (valor: unknown): valor is ContextoDeAbertura {
  if (typeof valor !== 'object' || valor === null) return false
  const contexto = valor as Record<string, unknown>
  return contexto.versao === 1 &&
    ['pessoaId', 'empresaId', 'sessaoId', 'interacaoId', 'identificadorRota']
      .every(chave => typeof contexto[chave] === 'string' && String(contexto[chave]).trim() !== '') &&
    (contexto.identificadorTela === undefined || typeof contexto.identificadorTela === 'string') &&
    (contexto.rascunhoOpaco === undefined || typeof contexto.rascunhoOpaco === 'string')
}

/** A retirada acontece antes de qualquer espera ou consumo, inclusive na rota já vigente. */
export function capturarContextoDeAbertura (historico: HistoricoComEstado): ContextoDeAbertura | null {
  const estado = historico.state || {}
  if (!Object.prototype.hasOwnProperty.call(estado, 'assistentePontotelAbertura')) return null
  const { assistentePontotelAbertura, ...restante } = estado
  historico.replaceState(restante, '')
  return contextoValido(assistentePontotelAbertura) ? assistentePontotelAbertura : null
}

export interface OpcoesDoProcessamentoDaAbertura {
  /** Reconfere rota, pessoa, empresa e conversa em cada fronteira assíncrona. */
  valida: () => boolean
  consumir: (identificadorTela: string, handle: string) => Promise<Record<string, unknown> | null>
  aplicar: (rascunho: Record<string, unknown>, ativo: () => boolean) => Promise<ResultadoDoPreenchimento>
  relatar: (sessaoId: string, interacaoId: string, resultado: ResultadoDoPreenchimento) => Promise<void>
}

export type EstadoDoProcessamento =
  | { estado: 'aplicado', resultado: ResultadoDoPreenchimento }
  | { estado: 'nao_aplicado' }
  | { estado: 'cancelado' }

export async function processarContextoDeAbertura (
  contexto: ContextoDeAbertura,
  opcoes: OpcoesDoProcessamentoDaAbertura
): Promise<EstadoDoProcessamento> {
  if (!opcoes.valida()) return { estado: 'nao_aplicado' }
  if (!contexto.rascunhoOpaco) return { estado: 'nao_aplicado' }

  const chaveDaTela = contexto.identificadorTela || contexto.identificadorRota
  let rascunho: Record<string, unknown> | null
  try {
    rascunho = await opcoes.consumir(chaveDaTela, contexto.rascunhoOpaco)
  } catch {
    rascunho = null
  }
  if (!opcoes.valida()) return { estado: 'cancelado' }
  if (!rascunho) return { estado: 'nao_aplicado' }

  let resultado: ResultadoDoPreenchimento
  try {
    resultado = await opcoes.aplicar(rascunho, opcoes.valida)
  } catch {
    return opcoes.valida() ? { estado: 'nao_aplicado' } : { estado: 'cancelado' }
  }
  if (!opcoes.valida()) return { estado: 'cancelado' }
  if (resultado.aplicados.length || resultado.recusados.length) {
    try {
      await opcoes.relatar(contexto.sessaoId, contexto.interacaoId, resultado)
    } catch {
      // O relato é informativo; a barra ainda deve permitir conferir o formulário.
    }
  }
  return { estado: 'aplicado', resultado }
}
